/**
 * @description Channel Partner primary vanity connect (LYN-340)
 *
 * Key features:
 * - One base → CNAME admin + app + portal + * (or child {slug}.{base}) + *.sites
 *   → tenant.lynx.build. Sheet is SoT for customer DNS (LYN-349).
 * - Connect / records / Verify / 30s GET poll / disconnect
 * - Same adminApi writer CP chrome uses via own-row routes
 *
 * @dependencies adminApi custom-domain status/register/verify/delete
 *
 * @notes
 * - Sending domain stays a separate sheet. Factory *.{slug} DNS is not deleted.
 * - Minimal copy. Do not hardcode a partner slug.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Copy,
  Globe,
  Link2,
  Loader2,
  RefreshCw,
  Trash2
} from 'lucide-react';
import {
  disconnectChannelPartnerCustomDomain,
  getChannelPartnerCustomDomainStatus,
  recheckChannelPartnerCustomDomain,
  registerChannelPartnerCustomDomain,
  verifyChannelPartnerCustomDomain
} from '../../services/api/adminApi';

export const PRIMARY_DOMAIN_POLL_MS = 30000;

const STEPS = {
  CONNECT: 'connect',
  DNS: 'dns',
  VERIFIED: 'verified'
};

function CopyButton({ value, label }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
      aria-label={label}
    >
      {copied ? (
        <>
          <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
          Copied
        </>
      ) : (
        <>
          <Copy className="h-3.5 w-3.5" />
          Copy
        </>
      )}
    </button>
  );
}

function DnsRecordRow({ record }) {
  const showProxy = Boolean(record.proxyStatus);

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          {record.type} record
        </span>
        <span className="text-xs text-gray-500">{record.label}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1">Host / Name</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 font-mono text-gray-900 break-all">{record.hostname}</code>
            <CopyButton value={record.hostname} label="Copy hostname" />
          </div>
        </div>
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1">Value</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 font-mono text-gray-900 break-all">{record.value}</code>
            <CopyButton value={record.value} label="Copy value" />
          </div>
        </div>
        {showProxy && (
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Proxy status</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 font-mono text-gray-900 break-all">{record.proxyStatus}</code>
              <CopyButton value={record.proxyStatus} label="Copy proxy status" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * @param {object} props
 * @param {string} props.channelPartnerId
 * @param {{
 *   getStatus: Function,
 *   register: Function,
 *   verify: Function,
 *   disconnect: Function
 * }} [props.api]
 */
export default function ChannelPartnerPrimaryDomainPanel({ channelPartnerId, api }) {
  const client = useMemo(() => api || {
    getStatus: () => getChannelPartnerCustomDomainStatus(channelPartnerId),
    register: (domainBase) => registerChannelPartnerCustomDomain(channelPartnerId, domainBase),
    verify: () => verifyChannelPartnerCustomDomain(channelPartnerId),
    recheck: () => recheckChannelPartnerCustomDomain(channelPartnerId),
    disconnect: () => disconnectChannelPartnerCustomDomain(channelPartnerId)
  }, [api, channelPartnerId]);

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [domainInput, setDomainInput] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  const applyPayload = useCallback((data) => {
    setPayload(data || null);
    if (data?.customDomainBase) {
      setDomainInput(data.customDomainBase);
    }
  }, []);

  const loadStatus = useCallback(async () => {
    const result = await client.getStatus();
    applyPayload(result.data || result);
    return result.data || result;
  }, [applyPayload, client]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadStatus()
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.error?.message || 'Failed to load primary domain');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [loadStatus]);

  const status = payload?.status || 'none';
  const isVerified = status === 'verified';
  const isPending = status === 'pending' || status === 'failed';
  const records = payload?.cnameInstructions?.records || [];
  const step = isVerified ? STEPS.VERIFIED : (isPending ? STEPS.DNS : STEPS.CONNECT);

  useEffect(() => {
    if (!isPending) {
      return undefined;
    }
    const intervalId = setInterval(() => {
      client.getStatus()
        .then((result) => applyPayload(result.data || result))
        .catch(() => {});
    }, PRIMARY_DOMAIN_POLL_MS);
    return () => clearInterval(intervalId);
  }, [applyPayload, client, isPending]);

  const handleConnect = async (event) => {
    event.preventDefault();
    setConnecting(true);
    setError(null);
    try {
      const result = await client.register(domainInput.trim());
      applyPayload(result.data || result);
      if (result.success === false && result.error?.message) {
        setError(result.error.message);
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to connect primary domain');
    } finally {
      setConnecting(false);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    setError(null);
    try {
      const result = await client.verify();
      applyPayload(result.data || result);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to verify primary domain');
    } finally {
      setVerifying(false);
    }
  };

  const handleRecheck = async () => {
    if (typeof client.recheck !== 'function') return;
    setRechecking(true);
    setError(null);
    try {
      const result = await client.recheck();
      applyPayload(result.data || result);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to re-check child doors');
    } finally {
      setRechecking(false);
    }
  };

  const pendingChildNests = Number(payload?.pendingChildNests || 0) > 0;

  const handleDisconnect = async () => {
    setDisconnecting(true);
    setError(null);
    try {
      const result = await client.disconnect();
      applyPayload(result.data || { status: 'none', customDomainBase: null, cnameInstructions: null });
      if (result.success === false && result.error?.message) {
        setError(result.error.message);
      } else {
        setDomainInput('');
        setShowDisconnectConfirm(false);
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to disconnect primary domain');
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-8 flex justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl">
      <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
        <Globe className="h-4 w-4 text-gray-500" />
        <div>
          <h2 className="text-sm font-medium text-gray-900">Primary domain</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Connect one domain. Add * for nest Sales Org hosts unless the zone already has *. Named admin, app, and portal win over *. Login verifies those three. *.sites is for customer websites.
          </p>
        </div>
      </div>

      <div className="px-5 py-4 space-y-4">
        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {step === STEPS.CONNECT && (
          <form onSubmit={handleConnect} className="space-y-3">
            <label htmlFor="cp-primary-domain" className="block text-sm font-medium text-gray-700">
              Primary domain
            </label>
            <p className="text-xs text-gray-500">
              Enter the base domain only, for example
              {' '}
              <span className="font-mono">theirbrand.com</span>
              .
            </p>
            <input
              id="cp-primary-domain"
              type="text"
              value={domainInput}
              onChange={(event) => setDomainInput(event.target.value)}
              placeholder="theirbrand.com"
              className="block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm font-mono shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              autoComplete="off"
              spellCheck={false}
            />
            <button
              type="submit"
              disabled={connecting || !domainInput.trim()}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-500 text-sm font-medium disabled:opacity-60"
            >
              {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
              Connect
            </button>
          </form>
        )}

        {step === STEPS.DNS && (
          <div className="space-y-4">
            <div className={`rounded-lg border p-4 flex items-start gap-3 ${
              status === 'failed' ? 'border-red-200 bg-red-50' : 'border-blue-200 bg-blue-50'
            }`}
            >
              {status === 'failed' ? (
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              ) : (
                <Loader2 className="h-5 w-5 text-blue-600 animate-spin flex-shrink-0 mt-0.5" />
              )}
              <div>
                <p className={`text-sm font-medium ${status === 'failed' ? 'text-red-900' : 'text-blue-900'}`}>
                  {status === 'failed' ? 'Registration failed' : 'Waiting for DNS'}
                </p>
                <p className={`text-sm mt-1 ${status === 'failed' ? 'text-red-800' : 'text-blue-800'}`}>
                  {payload?.cnameInstructions?.nestMode === 'children'
                    ? 'This zone already has a * record. Add the named Sales Org hosts below, plus admin, app, and portal. Login verifies admin, app, and portal. *.sites is stores only.'
                    : 'Add CNAME records for admin, app, portal, and *. Login verifies admin, app, and portal. *.sites is stores only.'}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {records.map((record) => (
                <DnsRecordRow key={`${record.type}-${record.hostname}-${record.value}`} record={record} />
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleVerify}
                disabled={verifying}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-500 text-sm font-medium disabled:opacity-60"
              >
                {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Verify
              </button>
              {pendingChildNests && (
                <button
                  type="button"
                  onClick={handleRecheck}
                  disabled={rechecking}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-indigo-200 text-indigo-700 rounded-lg hover:bg-indigo-50 text-sm font-medium disabled:opacity-60"
                >
                  {rechecking ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  Re-check child doors
                </button>
              )}
              {!showDisconnectConfirm ? (
                <button
                  type="button"
                  onClick={() => setShowDisconnectConfirm(true)}
                  className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                  Disconnect
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleDisconnect}
                    disabled={disconnecting}
                    className="text-sm font-medium text-red-700 hover:text-red-800 disabled:opacity-50"
                  >
                    {disconnecting ? 'Removing…' : 'Confirm disconnect'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDisconnectConfirm(false)}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {step === STEPS.VERIFIED && (
          <div className="rounded-xl border border-green-200 bg-green-50 p-5">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-green-900">Primary domain verified</h3>
                <p className="text-sm text-green-800 mt-1">
                  Login uses
                  {' '}
                  <span className="font-mono">{payload?.hostnames?.admin}</span>
                  {', '}
                  <span className="font-mono">{payload?.hostnames?.app}</span>
                  {', and '}
                  <span className="font-mono">{payload?.hostnames?.portal}</span>
                  .
                </p>
                {pendingChildNests && (
                  <button
                    type="button"
                    onClick={handleRecheck}
                    disabled={rechecking}
                    className="mt-4 mr-4 inline-flex items-center gap-2 text-sm font-medium text-indigo-700 hover:text-indigo-800 disabled:opacity-60"
                  >
                    {rechecking ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    Re-check child doors
                  </button>
                )}
                {!showDisconnectConfirm ? (
                  <button
                    type="button"
                    onClick={() => setShowDisconnectConfirm(true)}
                    className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-red-700 hover:text-red-800"
                  >
                    <Trash2 className="h-4 w-4" />
                    Disconnect
                  </button>
                ) : (
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <span className="text-sm text-green-900">Remove this domain and return login to the factory hosts?</span>
                    <button
                      type="button"
                      onClick={handleDisconnect}
                      disabled={disconnecting}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-500 disabled:opacity-50"
                    >
                      {disconnecting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      Confirm disconnect
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowDisconnectConfirm(false)}
                      disabled={disconnecting}
                      className="text-sm text-gray-600 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
